import type { LibraryBlockTypeForListPage } from '@ContractBuilder/types';
import type { Team } from '@domain/schemas/team.schema';
import type { BaseUser } from '@helpers/auth/types';
import { isClauseBlock } from '@root/helpers/blocks';
import Nav from '@root/src/routes';
import { type CellContext, createColumnHelper } from '@tanstack/react-table';
import { BLOCK_LAYOUTS_VALUE_TO_LABEL_MAPPING } from '@User/Blocks/constants';

import { effectiveToAccessor } from './shared/effectiveToAccessor';
import {
  classOfBusinessAccessor,
  createdAtAccessor,
  createdByAccessor,
  editDisplay,
  effectiveFromAccessor,
  lastUpdatedByAccessor,
  nameAccessor,
  statusAccessor,
  teamsAccessor,
  toggleSubRowsDisplay,
  updatedAtAccessor,
  usedInTemplatesAccessor,
} from './shared';
const columnHelper = createColumnHelper<LibraryBlockTypeForListPage>();

export const getBlocksTableColumns = ({ user, teams }: { user?: BaseUser | null; teams?: Team[] } = {}) => [
  toggleSubRowsDisplay(columnHelper),
  columnHelper.accessor('id', {
    id: 'id',
    enableColumnFilter: false,
    enableHiding: false,
    size: 80,
    minSize: 80,
    maxSize: 80,
  }),
  nameAccessor(columnHelper, 'Block Name', { order: 0, placeholder: 'All blocks' }),
  columnHelper.accessor('type', {
    id: 'type',
    header: 'Layout',
    size: 160,
    minSize: 160,
    maxSize: 160,
    cell: (cellContext: CellContext<LibraryBlockTypeForListPage, string>) =>
      BLOCK_LAYOUTS_VALUE_TO_LABEL_MAPPING.find((i) => i.value === cellContext.row.original.type)?.label ?? '',
    sortingFn: 'text',
    sortUndefined: -1,
    filterFn: (row, _, selectedFilters) => {
      if ((selectedFilters?.length ?? 0) === 0) {
        return true;
      }
      return selectedFilters.includes(
        BLOCK_LAYOUTS_VALUE_TO_LABEL_MAPPING.find((i) => i.value === row.original.type)?.label ?? '',
      );
    },
    meta: {
      filter: {
        options: BLOCK_LAYOUTS_VALUE_TO_LABEL_MAPPING.filter(({ value }) => !isClauseBlock({ type: value })).map(
          ({ label }) => label,
        ),
        order: 1,
        placeholder: 'All layouts',
      },
      tooltip: true,
      headingCellClassName: 'w-40',
    },
  }),
  statusAccessor(columnHelper),
  usedInTemplatesAccessor(columnHelper),
  effectiveToAccessor(columnHelper),
  lastUpdatedByAccessor(columnHelper),
  updatedAtAccessor(columnHelper),
  createdByAccessor(columnHelper),
  createdAtAccessor(columnHelper),
  effectiveFromAccessor(columnHelper),
  classOfBusinessAccessor(columnHelper),
  teamsAccessor(columnHelper, teams),
  editDisplay(columnHelper, Nav.Block, user),
];
